// Rechte-Auflösung für den Admin-Bereich (Roadmap 1.21, Teil 1). Reine Funktionen, ohne React,
// damit Leiste, Seitenschutz und der Vorher-Nachher-Vergleich (scripts/check-admin-nav.ts) dieselbe
// Logik benutzen.
//
// Regel je Rolle: eigene page_access-Zeile der Seite → zählt (an oder aus).
// Keine eigene Zeile → Zeile der Gruppe. Keine von beiden → aus.
// Über mehrere Rollen einer Person wird vereinigt (eine Rolle reicht). Founder sieht alles.
import { matchPath } from "react-router-dom";
import { ADMIN_NAV, pageRoutes, type AdminNavGroup, type AdminNavPage } from "@/config/adminNav";

export type AccessIndex = Map<string, Map<string, boolean>>;

export function buildAccessIndex(rows: { page_key: string; role: string; can_view: boolean }[]): AccessIndex {
  const index: AccessIndex = new Map();
  for (const r of rows) {
    let perRole = index.get(r.page_key);
    if (!perRole) { perRole = new Map(); index.set(r.page_key, perRole); }
    perRole.set(r.role, r.can_view);
  }
  return index;
}

export interface AccessContext {
  index: AccessIndex;
  roles: Set<string>;
  isFounder: boolean;
}

/** Bisherige Regel für einen einzelnen Schlüssel (ohne Vererbung). */
export function canViewKey(ctx: AccessContext, key: string): boolean {
  if (ctx.isFounder) return true;
  const perRole = ctx.index.get(key);
  if (!perRole) return false;
  for (const role of ctx.roles) if (perRole.get(role) === true) return true;
  return false;
}

/** Seite mit Vererbung von der Gruppe. */
export function canViewPage(ctx: AccessContext, pageKey: string, groupKey?: string): boolean {
  if (ctx.isFounder) return true;
  const own = ctx.index.get(pageKey);
  const group = groupKey ? ctx.index.get(groupKey) : undefined;
  for (const role of ctx.roles) {
    const ownValue = own?.get(role);
    if (ownValue !== undefined) {
      if (ownValue) return true;
      continue;
    }
    if (group?.get(role) === true) return true;
  }
  return false;
}

/**
 * Kette mit Vererbung (CO-8, Bereiche innerhalb einer Seite). keys = [spezifischster … Gruppe].
 * Je Rolle entscheidet der erste Schlüssel mit eigener Zeile; über Rollen vereinigt; Founder sieht alles.
 * canViewPage ist der Sonderfall [Seite, Gruppe] und bleibt bewusst unverändert.
 */
export function canViewChain(ctx: AccessContext, keys: string[]): boolean {
  if (ctx.isFounder) return true;
  for (const role of ctx.roles) {
    for (const key of keys) {
      const v = ctx.index.get(key)?.get(role);
      if (v === undefined) continue;
      if (v) return true;
      break;
    }
  }
  return false;
}

/** Vererbungskette eines Bereichs-Schlüssels aus ADMIN_NAV: [Unterbereich?, Bereich, Seite, Gruppe]. */
export function bereichKette(key: string, nav: AdminNavGroup[] = ADMIN_NAV): string[] | undefined {
  for (const g of nav) {
    for (const p of g.pages) {
      for (const b of p.bereiche ?? []) {
        if (b.key === key) return [b.key, p.key, g.key];
        if (b.unter?.some((u) => u.key === key)) return [key, b.key, p.key, g.key];
      }
    }
  }
  return undefined;
}

/** Bereich innerhalb einer Seite sichtbar? Unbekannter Schlüssel = gesperrt (fail closed). */
export function canViewBereich(ctx: AccessContext, key: string, nav: AdminNavGroup[] = ADMIN_NAV): boolean {
  const kette = bereichKette(key, nav);
  return kette ? canViewChain(ctx, kette) : false;
}

/** Hat eine meiner Rollen eine eigene Zeile für diese Seite? */
export function hasOwnRow(ctx: AccessContext, pageKey: string): boolean {
  const own = ctx.index.get(pageKey);
  if (!own) return false;
  for (const role of ctx.roles) if (own.has(role)) return true;
  return false;
}

// ── Leiste ──

export interface NavLeaf {
  icon: AdminNavPage["icon"];
  label: string;
  path: string;
  badgeKey?: AdminNavPage["badgeKey"];
}
export interface NavBranch {
  icon: AdminNavGroup["icon"];
  label: string;
  items: NavLeaf[];
}
export type NavEntry = NavLeaf | NavBranch;
export const isNavBranch = (e: NavEntry): e is NavBranch => "items" in e;

const leaf = (p: AdminNavPage): NavLeaf => ({ icon: p.icon, label: p.label, path: p.path, badgeKey: p.badgeKey });

/**
 * Sichtbare Leiste: Seite mit Vererbung von der Gruppe, für alle Rollen gleich.
 * Seit Teil 3 (04.10.2026) ohne Walker-Sonderlogik: auch Walker sehen genau, was page_access erlaubt.
 */
export function visibleNav(ctx: AccessContext, nav: AdminNavGroup[] = ADMIN_NAV): NavEntry[] {
  const result: NavEntry[] = [];

  for (const g of nav) {
    if (g.hidden) continue;
    const pages = g.pages.filter((p) => !p.hidden && (p.rechteWie
      ? canViewPage(ctx, p.rechteWie.key, p.rechteWie.group)
      : canViewPage(ctx, p.key, g.key)));
    if (g.flat) {
      result.push(...pages.map(leaf));
    } else if (pages.length > 0) {
      result.push({ icon: g.icon, label: g.label, items: pages.map(leaf) });
    }
  }
  return result;
}

// ── Seitenschutz ──

/** Seite + Gruppe, die eine /admin-Adresse schützt. undefined = Adresse ist nicht registriert. */
export function findPageForPath(pathname: string, nav: AdminNavGroup[] = ADMIN_NAV): { page: AdminNavPage; group: AdminNavGroup } | undefined {
  for (const group of nav) {
    for (const page of group.pages) {
      for (const route of pageRoutes(page)) {
        if (matchPath({ path: `/admin/${route}`, end: true }, pathname)) return { page, group };
      }
    }
  }
  return undefined;
}

/** Darf die Person diese Adresse öffnen? Nicht registrierte Adressen sind gesperrt (fail closed). */
export function canViewPath(ctx: AccessContext, pathname: string, nav: AdminNavGroup[] = ADMIN_NAV): boolean {
  const hit = findPageForPath(pathname, nav);
  if (!hit) return false;
  return canViewPage(ctx, hit.page.key, hit.group.key);
}

// ── Produkte je Person (Roadmap 1.2b) ──
// Seiten mit Umschalter Gassi | Daycare zeigen nur die Produkte der Person. Quelle ist die bestehende
// Rollen-Zuweisung (staff_role_assignments.product_id), keine eigene Tab-Rechte-Ebene.
// Regel: Founder/Admin = alle. Eine Zuweisung ohne Produkt (NULL) = alle. Sonst die Produkte der Zuweisungen.
// Gleiche Regel wie can_access_product() in der Datenbank.

export type AdminProdukt = "gassi" | "daycare";
export const ADMIN_PRODUKTE: readonly AdminProdukt[] = ["gassi", "daycare"];

export interface ProduktZuweisung {
  role: string;
  /** products.key der Zuweisung, null = alle Produkte */
  productKey: string | null;
}

export function erlaubteProdukte(zuweisungen: ProduktZuweisung[]): Set<AdminProdukt> {
  const ergebnis = new Set<AdminProdukt>();
  for (const z of zuweisungen) {
    if (z.role === "founder" || z.role === "admin" || z.productKey === null) return new Set(ADMIN_PRODUKTE);
    if ((ADMIN_PRODUKTE as readonly string[]).includes(z.productKey)) ergebnis.add(z.productKey as AdminProdukt);
  }
  return ergebnis;
}

/** Gewünschtes Produkt, falls erlaubt; sonst das erste erlaubte (Gassi vor Daycare). null = keins erlaubt. */
export function wirksamesProdukt(gewuenscht: AdminProdukt, erlaubt: Set<AdminProdukt>): AdminProdukt | null {
  if (erlaubt.has(gewuenscht)) return gewuenscht;
  return ADMIN_PRODUKTE.find((p) => erlaubt.has(p)) ?? null;
}
